import { FC, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { callApi, HttpMethod } from "src/services/apiService";
import { runtimeConfig } from "src/services/runtimeConfig";

import {
  Grid,
  GridColumn as Column,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from "@progress/kendo-react-grid";
import ReactDOM from "react-dom";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";

import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Window } from "@progress/kendo-react-dialogs";
import ConfirmationModal from "src/components/ConfirmationModal";

type AdjustmentDTO = {
  id: number;
  adjustmentNum: string;
  accountNumId: number;
  acctId: string;
  accountName: string;
  invoiceId: number | null;
  invoiceNum: string | null;
  purchaseText: string | null;
  total: number;
  receivedOn: Date | null;
  notes: string;
};

const loadingPanelMarkup = (
  <div className="k-loading-mask">
    <span className="k-loading-text">Loading</span>
    <div className="k-loading-image" />
    <div className="k-loading-color" />
  </div>
);

const LoadingPanel = (props: any) => {
  const { gridRef } = props;
  const gridContent =
    gridRef.current && gridRef.current.querySelector(".k-grid-content");
  return gridContent
    ? ReactDOM.createPortal(loadingPanelMarkup, gridContent)
    : loadingPanelMarkup;
};

interface PageState {
  skip: number;
  take: number;
}

const initialFilter: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};
const initialPage: PageState = { skip: 0, take: 20 };

// Helper function to convert data to CSV format
const convertToCSV = (data: AdjustmentDTO[]): string => {
  if (!data || data.length === 0) return "";

  const headers = [
    "Account ID",
    "Account Name",
    "Adjustment Num",
    "Invoice ID",
    "Invoice Num",
    "Purchase",
    "Amount",
    "Notes",
    "Received On",
  ];

  const csvRows = data.map((item) => [
    item.acctId ?? "",
    `"${(item.accountName ?? "").replace(/"/g, '""')}"`,
    item.adjustmentNum ?? "",
    item.invoiceId ?? "",
    item.invoiceNum ?? "",
    `"${(item.purchaseText ?? "").replace(/"/g, '""')}"`,
    item.total ?? "",
    `"${(item.notes ?? "").replace(/"/g, '""')}"`,
    item.receivedOn ? item.receivedOn.toLocaleDateString() : "",
  ]);

  const csvContent = [
    headers.join(","),
    ...csvRows.map((row) => row.join(",")),
  ].join("\n");

  return csvContent;
};

const AcctIdCell: FC<any> = (props) => {
  const acctId = props.dataItem[props.field];
  const targetUrl = `${runtimeConfig.classicUrl}/app/sysadmin/accounts.adp?acctId=${acctId}`;

  return (
    <td>
      <a
        href={targetUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "#007bff",
          textDecoration: "underline",
          cursor: "pointer",
          fontWeight: "bold",
        }}
      >
        {acctId}
      </a>
    </td>
  );
};

const PurchaseCell: FC<any> = (props) => {
  const text = props.dataItem.purchaseText;
  if (!text) return <td><span className="text-muted">—</span></td>;
  return <td>{text}</td>;
};

const AmountCell: FC<any> = (props) => {
  const amount = props.dataItem[props.field];
  const formattedAmount = amount != null ? `$${amount.toFixed(2)}` : "$0.00";

  return <td>{formattedAmount}</td>;
};

const Adjustments: FC = () => {
  const [adjustments, setAdjustments] = useState<AdjustmentDTO[]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [error, setError] = useState<string>("");
  const gridRef = useRef(null);
  const [filter, setFilter] =
    useState<CompositeFilterDescriptor>(initialFilter);
  const [page, setPage] = useState<PageState>(initialPage);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(
    null,
  );

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<AdjustmentDTO | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Date range state - default to one year back
  const [startDate, setStartDate] = useState<Date>(() => {
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return oneYearAgo;
  });
  const [endDate, setEndDate] = useState<Date>(new Date());
  // Tracks the dates from the last successful load so the display text
  // stays in sync with the data actually shown in the grid.
  const [loadedStartDate, setLoadedStartDate] = useState<Date | null>(null);
  const [loadedEndDate, setLoadedEndDate] = useState<Date | null>(null);

  const loadData = async () => {
    setShowLoading(true);
    setError("");
    try {
      const response = await callApi(
        "sysadmin/adjustment/GetAdjustments",
        HttpMethod.POST,
        {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      );
      const formattedData = (response || []).map(
        (item: any, index: number) => ({
          ...item,
          receivedOn: item.receivedOn ? new Date(item.receivedOn) : null,
          uniqueId: `${item.acctId}-${item.adjustmentNum}-${index}`,
        }),
      );
      setAdjustments(formattedData);
      setLoadedStartDate(startDate);
      setLoadedEndDate(endDate);
      setShowLoading(false);
    } catch (err) {
      console.error("Error fetching adjustments:", err);
      const message = err instanceof Error ? err.message : JSON.stringify(err);
      setError(`Error fetching adjustments: ${message}`);
      setShowLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Modal handlers
  const handleInvoiceClick = useCallback((invoiceId: number) => {
    setSelectedInvoiceId(invoiceId);
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedInvoiceId(null);
  }, []);

  const handleDeleteClick = useCallback((item: AdjustmentDTO) => {
    setDeleteTarget(item);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const targetId = deleteTarget.id;
    setDeleteTarget(null);
    setDeletingId(targetId);
    try {
      await callApi(`sysadmin/adjustment/${targetId}`, HttpMethod.DELETE);
      setAdjustments((prev) => prev.filter((a) => a.id !== targetId));
    } catch (err) {
      console.error("Error deleting adjustment:", err);
      const message = err instanceof Error ? err.message : JSON.stringify(err);
      setError(`Error deleting adjustment: ${message}`);
    } finally {
      setDeletingId(null);
    }
  }, [deleteTarget]);

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setIsFiltering(true);
    setFilter(e.filter);
    setTimeout(() => {
      setIsFiltering(false);
    }, 100);
  };

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? adjustments.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  // Apply client-side filtering
  const filteredData = useMemo(() => {
    if (!filter || !filter.filters || filter.filters.length === 0) {
      return adjustments;
    }

    try {
      const processedFilter = {
        ...filter,
        filters: filter.filters.map((f: any) => {
          if (f.field === "receivedOn" && f.operator === "eq") {
            const filterDate = new Date(f.value);
            const startOfDay = new Date(
              filterDate.getFullYear(),
              filterDate.getMonth(),
              filterDate.getDate(),
            );
            const endOfDay = new Date(
              filterDate.getFullYear(),
              filterDate.getMonth(),
              filterDate.getDate(),
              23,
              59,
              59,
              999,
            );

            return {
              logic: "and",
              filters: [
                { field: "receivedOn", operator: "gte", value: startOfDay },
                { field: "receivedOn", operator: "lte", value: endOfDay },
              ],
            };
          }
          return f;
        }),
      };

      return filterBy(adjustments, processedFilter);
    } catch (error) {
      console.error("Filter error:", error);
      return adjustments;
    }
  }, [adjustments, filter]);

  // Clamp page.skip when filteredData shrinks (e.g. after delete on last page)
  useEffect(() => {
    if (filteredData.length === 0) {
      if (page.skip !== 0) setPage((prev) => ({ ...prev, skip: 0 }));
    } else if (page.skip >= filteredData.length) {
      const lastPageSkip =
        Math.floor((filteredData.length - 1) / page.take) * page.take;
      setPage((prev) => ({ ...prev, skip: lastPageSkip }));
    }
  }, [filteredData.length, page.skip, page.take]);

  const handleExportToExcel = useCallback(() => {
    const csvContent = convertToCSV(filteredData);

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `adjustments-export-${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [filteredData]);

  const pagedData = useMemo(() => {
    const state: { skip: number; take: number } = {
      skip: page.skip ?? 0,
      take: page.take ?? 20,
    };

    return filteredData.slice(state.skip, state.skip + state.take);
  }, [filteredData, page]);

  const InvoiceIdCell: FC<any> = useCallback(
    (props) => {
      const invoiceId = props.dataItem[props.field];
      if (!invoiceId) return <td></td>;

      return (
        <td>
          <button
            onClick={() => handleInvoiceClick(invoiceId)}
            style={{
              color: "#007bff",
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: "bold",
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
            }}
          >
            {invoiceId}
          </button>
        </td>
      );
    },
    [handleInvoiceClick],
  );

  const DeleteCell: FC<any> = useCallback(
    (props) => {
      const item = props.dataItem as AdjustmentDTO;
      const isThisDeleting = deletingId === item.id;
      return (
        <td style={{ textAlign: "center" }}>
          <button
            className="btn btn-outline-danger btn-sm"
            title="Delete adjustment"
            aria-label="Delete adjustment"
            onClick={() => handleDeleteClick(item)}
            disabled={deletingId !== null}
          >
            {isThisDeleting ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            ) : (
              <i className="bi bi-trash" />
            )}
          </button>
        </td>
      );
    },
    [handleDeleteClick, deletingId],
  );

  return (
    <>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      {/* Date Range Selector */}
      <div
        style={{
          padding: "10px",
          backgroundColor: "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "4px",
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "15px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <label style={{ fontWeight: "bold", minWidth: "80px" }}>
              From Date:
            </label>
            <DatePicker
              value={startDate}
              onChange={(e) => setStartDate(e.value || new Date())}
              format="MM/dd/yyyy"
              style={{ width: "150px" }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <label style={{ fontWeight: "bold", minWidth: "80px" }}>
              To Date:
            </label>
            <DatePicker
              value={endDate}
              onChange={(e) => setEndDate(e.value || new Date())}
              format="MM/dd/yyyy"
              style={{ width: "150px" }}
            />
          </div>
          <button
            onClick={loadData}
            className="btn btn-primary btn-sm"
            disabled={showLoading}
          >
            {showLoading ? "Searching..." : "Search"}
          </button>
          {loadedStartDate && loadedEndDate && (
            <div style={{ fontSize: "12px", color: "#6c757d" }}>
              Showing adjustments from {loadedStartDate.toLocaleDateString()} to{" "}
              {loadedEndDate.toLocaleDateString()}
            </div>
          )}
          <div style={{ marginLeft: "auto" }}>
            <button
              onClick={handleExportToExcel}
              className="btn btn-outline-secondary btn-sm d-flex align-items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-filetype-xlsx"
                viewBox="0 0 16 16"
              >
                <path
                  fillRule="evenodd"
                  d="M14 4.5V11h-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zM7.86 14.841a1.13 1.13 0 0 0 .401.823q.195.162.479.252.284.091.665.091.507 0 .858-.158.355-.158.54-.44a1.17 1.17 0 0 0 .187-.656q0-.336-.135-.56a1 1 0 0 0-.375-.357 2 2 0 0 0-.565-.21l-.621-.144a1 1 0 0 1-.405-.176.37.37 0 0 1-.143-.299q0-.234.184-.384.188-.152.513-.152.214 0 .37.068a.6.6 0 0 1 .245.181.56.56 0 0 1 .12.258h.75a1.1 1.1 0 0 0-.199-.566 1.2 1.2 0 0 0-.5-.41 1.8 1.8 0 0 0-.78-.152q-.44 0-.777.15-.336.149-.527.421-.19.273-.19.639 0 .302.123.524t.351.367q.229.143.54.213l.618.144q.31.073.462.193a.39.39 0 0 1 .153.326.5.5 0 0 1-.085.29.56.56 0 0 1-.255.193q-.168.07-.413.07-.176 0-.32-.04a.8.8 0 0 1-.249-.115.58.58 0 0 1-.255-.384zm-3.726-2.909h.893l-1.274 2.007 1.254 1.992h-.908l-.85-1.415h-.035l-.853 1.415H1.5l1.24-2.016-1.228-1.983h.931l.832 1.438h.036zm1.923 3.325h1.697v.674H5.266v-3.999h.791zm7.636-3.325h.893l-1.274 2.007 1.254 1.992h-.908l-.85-1.415h-.035l-.853 1.415h-.861l1.24-2.016-1.228-1.983h.931l.832 1.438h.036z"
                />
              </svg>
              Export to CSV
            </button>
          </div>
        </div>
      </div>

      <div ref={gridRef} style={{ padding: "5px" }}>
        {showLoading || isFiltering ? <LoadingPanel gridRef={gridRef} /> : null}
        <Grid
          className="sysadmin-grid"
          data={pagedData}
          dataItemKey="uniqueId"
          filterable={true}
          sortable={true}
          skip={page.skip}
          take={page.take}
          total={filteredData.length}
          pageable={{
            buttonCount: 4,
            pageSizes: [10, 20, 30, 40],
            pageSizeValue: pageSizeValue,
          }}
          onPageChange={pageChange}
          filter={filter}
          onFilterChange={handleFilterChange}
          style={{ height: "800px" }}
        >
          <Column
            field="acctId"
            title="Account ID"
            cell={AcctIdCell}
            filter="text"
          />
          <Column field="accountName" title="Account Name" filter="text" />
          <Column
            field="adjustmentNum"
            title="Adjustment Num"
            filter="text"
          />
          <Column
            field="invoiceId"
            title="Invoice ID"
            filter="numeric"
            cell={InvoiceIdCell}
          />
          <Column field="invoiceNum" title="Invoice Num" filter="text" />
          <Column field="purchaseText" title="Purchase" filter="text" cell={PurchaseCell} />
          <Column
            field="total"
            title="Amount"
            filter="numeric"
            cell={AmountCell}
          />
          <Column field="notes" width="480px" title="Notes" filter="text" />
          <Column
            field="receivedOn"
            title="Received On"
            width="220px"
            filter="date"
            format="{0:M/d/yyyy}"
          />
          <Column
            title=" "
            width="70px"
            filterable={false}
            sortable={false}
            cell={DeleteCell}
          />
        </Grid>
      </div>

      {/* Invoice Details Modal */}
      {isModalOpen && selectedInvoiceId && (
        <Window
          title={`Invoice Details - #${selectedInvoiceId}`}
          onClose={handleModalClose}
          height={700}
          width={900}
          resizable={true}
          draggable={true}
        >
          <div
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <iframe
              src={`${runtimeConfig.classicUrl}/app/admin/b-invoice.adp?id=${selectedInvoiceId}&fromSysadmin=1`}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                flex: 1,
              }}
              title={`Invoice ${selectedInvoiceId}`}
            />
          </div>
        </Window>
      )}

      <ConfirmationModal
        isVisible={deleteTarget !== null}
        title="Delete Adjustment"
        message={
          deleteTarget
            ? `Are you sure you want to delete adjustment ${deleteTarget.adjustmentNum}? This cannot be undone.`
            : ""
        }
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default Adjustments;
